import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';
import { useRegion } from '../../contexts/RegionContext';
import { HiSparkles, HiArrowNarrowLeft, HiArrowNarrowRight, HiDownload, HiPlay } from 'react-icons/hi';

interface GameItem {
  id: string;
  slug: string;
  title: string;
  description?: string;
  image_icon?: string;
  download_url?: string;
  status: 'released' | 'in_progress' | 'coming_soon';
  is_visible: boolean;
}

export default function LatestGamesSection() {
  const { t, direction } = useRegion();
  const [games, setGames] = useState<GameItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const { data, error } = await supabase
          .from('games')
          .select('id, slug, title, description, image_icon, download_url, status, is_visible')
          .eq('is_visible', true)
          .limit(3);

        if (error) throw error;
        setGames(data || []);
      } catch (err) {
        console.error('Error fetching latest games for home:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'released':
        return <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs px-2.5 py-1 rounded-full font-bold">{t('games.statusReleased')}</span>;
      case 'in_progress':
        return <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs px-2.5 py-1 rounded-full font-bold">{t('games.statusInProgress')}</span>;
      default:
        return <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs px-2.5 py-1 rounded-full font-bold">{t('games.statusComingSoon')}</span>;
    }
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-black via-zinc-950 to-black relative z-10 border-t border-white/5" dir={direction}>
      <div className="container mx-auto px-4 md:px-8">
        {/* هدر بخش */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-12">
          <div>
            <div className="flex items-center gap-2 text-primary font-bold text-xs md:text-sm mb-2">
              <HiSparkles className="w-4 h-4" />
              <span>Active Legend Studio</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-white tracking-tight">
              {t('games.latestTitle')}
            </h2>
          </div>
          <Link
            to="/games"
            className="inline-flex items-center gap-2 text-primary hover:text-white font-bold text-sm group transition-colors"
          >
            <span>{t('games.viewAllGames')}</span>
            {direction === 'rtl' ? (
              <HiArrowNarrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            ) : (
              <HiArrowNarrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            )}
          </Link>
        </div>

        {/* لیست بازی‌ها */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-72 rounded-2xl bg-white/5 animate-pulse border border-white/10" />
            ))}
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-12 rounded-2xl bg-white/5 border border-white/10">
            <p className="text-gray-400 text-sm">بازی جدیدی یافت نشد.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {games.map((game) => (
              <motion.div
                key={game.id}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.2 }}
                className="group relative bg-zinc-900/80 rounded-3xl overflow-hidden border border-white/10 hover:border-primary/50 shadow-xl hover:shadow-primary/10 transition-all flex flex-col"
              >
                {/* کاور بازی */}
                <div className="relative aspect-video w-full overflow-hidden bg-black/40">
                  <img
                    src={game.image_icon || '/AE logo.svg'}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.currentTarget.src = '/AE logo.svg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent opacity-80" />
                  <div className="absolute top-4 right-4">
                    {getStatusBadge(game.status)}
                  </div>
                </div>

                {/* محتوای کارت */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-black text-white group-hover:text-primary transition-colors mb-2">
                      {game.title}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm line-clamp-2 leading-relaxed mb-6">
                      {game.description || 'تجربه یک بازی هیجان‌انگیز، سریع و چالش‌برانگیز در دنیای اکتیو لجند.'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <Link
                      to={`/games/${game.slug}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-black font-extrabold text-xs md:text-sm py-2.5 px-4 rounded-xl transition-all active:scale-95"
                    >
                      <HiPlay className="w-4 h-4" />
                      <span>{t('games.gamePage')}</span>
                    </Link>
                    {game.download_url && game.status !== 'coming_soon' && (
                      <a
                        href={game.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                        title={t('games.directDownload')}
                      >
                        <HiDownload className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
